/**
 * A collection of functions and components that can be useful with Mapbox
 * TODO: mvoe all of that into a common dependency with the backoffice.
 */
import { useState, useEffect, useCallback } from 'react'
import type { FC } from 'react'
import { MapLayerMouseEvent, useMap } from 'react-map-gl'
import type { LngLatBoundsLike, MapboxGeoJSONFeature } from 'react-map-gl'

function boundsFromFlatArray(input: number[]): LngLatBoundsLike {
	return input.reduce(
		function (result, value, index) {
			const isLat = index % 2
			if (isLat) {
				if (value < result[1]) {
					result.splice(1, 1, value)
				}
				if (value > result[3]) {
					result.splice(3, 1, value)
				}
			} else {
				if (value < result[0]) {
					result.splice(0, 1, value)
				}
				if (value > result[2]) {
					result.splice(2, 1, value)
				}
			}
			return result
		},
		[Infinity, Infinity, -Infinity, -Infinity]
	)
}

function getBounds(featureList: MapboxGeoJSONFeature[]): LngLatBoundsLike {
	return boundsFromFlatArray(
		featureList
			.map((feature) => {
				if (feature.geometry.type === 'Polygon') {
					return feature.geometry.coordinates
				}
				return []
			})
			.filter((coordinates) => coordinates.length > 0)
			.flat(6)
	)
}

/**
 * This component is not present in react-map-gl by default but it facilitates a lot handling dynamic properties
 * such as hovering state for instance.
 * It handles automatic updates of Mapbox setFeatureState for a given feature. This allows dynamic styling to be possible without changing the top
 * style component or changing the layers themselves.
 * If put under a Source component, the source property will be filled automatically, otherwise it is required.
 */
const FeatureState: FC<{
	state: { hover: boolean }
	source: string
	sourceLayer: string
	id: string
}> = ({ state, source, sourceLayer, id }) => {
	if (!source || !sourceLayer) {
		throw new Error(
			'Source is required (directly or through a Source component)'
		)
	}

	const { current: map } = useMap()
	useEffect(() => {
		if (!map) {
			return
		}
		const feature = { source, sourceLayer, id }
		map.setFeatureState(feature, state)
		return () => {
			map.removeFeatureState(feature)
		}
	}, [map, id, source, sourceLayer, state])
	return <></>
}

/**
 * This component is made to simplify the handling of hovering in layer.
 * Simply put it inside a map with the right source and layer,
 * and the current feature that is being hovered will have a state {hover: true} activated
 * ideally we would have hooked it up to Mapbox events but unfortunately the react-map-gl component sometimes has the overlay on top
 * and handles the event through an eventManager on top, so in that case we would not receive anything. This is why
 * this component is hooked to the react-map-gl eventmanager (imported from mjolnir) instead.
 */
const HoverInjector: FC<{
	source: string | undefined
	layer: string
	sourceLayer: string
}> = ({ source, layer, sourceLayer }) => {
	if (!source) {
		throw new Error(
			'Source is required (directly or through a Source component)'
		)
	}
	const [hovered, setHover] = useState<string | null>(null)
	const { current: map } = useMap()
	const handleMouseMove = useCallback(
		(e: MapLayerMouseEvent & { offsetCenter: { x: number; y: number } }) => {
			if (!map) {
				return
			}
			const features = map.queryRenderedFeatures(
				[e.offsetCenter.x, e.offsetCenter.y],
				{ layers: [layer] }
			)
			if (features.length === 0) {
				if (hovered) {
					setHover(null)
				}
				return
			}
			const hoveredId = features[0]?.id
			setHover(hoveredId?.toString() || null)
		},
		[map, layer, hovered]
	)

	useEffect(() => {
		if (!map) {
			return
		}
		map.on('pointermove', handleMouseMove)
		return () => {
			map.off('pointermove', handleMouseMove)
		}
	}, [map, handleMouseMove])

	// Reset the hovering state in case the layer or source is ever changed in this component
	useEffect(() => {
		setHover(null)
		return
	}, [source, layer, sourceLayer])

	if (hovered === null) {
		return null
	}
	return (
		<FeatureState
			source={source}
			state={{ hover: true }}
			id={hovered}
			sourceLayer={sourceLayer}
		/>
	)
}

export { getBounds, HoverInjector, FeatureState }
